import {
  DataTable,
  Tag,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import { Code, Download, FilePlus2, Mail, RefreshCw, RotateCcw, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDateTimeToBrazil } from '../../../shared/utils';
import { useImplantationKitDownload } from '../hooks/use-implantation-kit-download.hook';
import { useIntegrationDetails } from '../hooks/use-integration-details.hook';
import { useManualEmissionAction } from '../hooks/use-manual-emission-action.hook';
import { useReprocessIntegration } from '../hooks/use-reprocess-integration.hook';
import { ConfirmActionModal } from '@/shared/ui/confirm-action-modal.component';
import { useReleaseToAutomaticAction } from '../hooks/use-release-to-automatic-action.hook';
import { ManualEmissionModal } from './manual-emission-modal.component';
import { JsonViewerModal } from './json-viewer-modal.component';
import type { IntegrationHistory } from '../../../services/integrations/types';
import { Page } from '../../../shared/ui/page';
import { CopyableField } from '../../../shared/ui';
// Alias curto de propósito: os nomes exportados são longos e, usados direto no
// JSX, estouram o printWidth de 80 e forçam o prettier a reflowar a tabela toda.
import {
  EMISSION_CONTROL_AND_HISTORY_STATUS_CONFIG as statusConfig,
  EMISSION_CONTROL_STEP_LABELS as stepLabels,
  INTEGRATION_FLOW_LABELS as flowLabels,
  INTEGRATION_PROVIDER_LABELS as providerLabels,
} from '../constants/emission-control-labels.constants';

interface IntegrationDetailsPageProps {
  policyId: string;
}

function createHistoryColumns(
  onViewJson: (item: IntegrationHistory) => void,
): DataTableColumn<IntegrationHistory>[] {
  return [
    {
      accessorKey: 'createdAt',
      header: 'DATA',
      size: 170,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {formatDateTimeToBrazil(row.original.createdAt)}
        </span>
      ),
    },
    {
      accessorKey: 'flow',
      header: 'FLUXO',
      size: 100,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {flowLabels[row.original.flow] ?? row.original.flow}
        </span>
      ),
    },
    {
      accessorKey: 'step',
      header: 'ETAPA',
      size: 220,
      cell: ({ row }) => {
        const stepLabel = stepLabels[row.original.step] ?? row.original.step;
        const description = row.original.description;
        return (
          <div className='flex flex-col'>
            <span className='text-alba-gray-700 text-sm'>{stepLabel}</span>
            {description && (
              <span className='text-alba-gray-500 text-xs'>{description}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'provider',
      header: 'PROVIDER',
      size: 120,
      cell: ({ row }) => (
        <Tag.Root variant='outline' colorScheme='gray' size='sm'>
          <Tag.Label>{providerLabels[row.original.provider] ?? row.original.provider}</Tag.Label>
        </Tag.Root>
      ),
    },
    {
      accessorKey: 'status',
      header: 'STATUS',
      size: 110,
      cell: ({ row }) => {
        const config = statusConfig[row.original.status] ?? {
          label: row.original.status,
          colorScheme: 'gray' as const,
        };
        return (
          <Tag.Root variant='outline' colorScheme={config.colorScheme} size='sm'>
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      accessorKey: 'errorMessage',
      header: 'ERRO',
      size: 200,
      cell: ({ row }) => {
        const msg = row.original.errorMessage;
        if (!msg) return <span className='text-alba-gray-400 text-sm'>-</span>;
        return (
          <span className='text-red-600 text-sm truncate block max-w-[200px]' title={msg}>
            {msg}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'JSON',
      size: 60,
      cell: ({ row }) => {
        const hasPayload = row.original.requestPayload || row.original.responsePayload;
        if (!hasPayload) return <span className='text-alba-gray-400 text-sm'>-</span>;
        return (
          <button
            type='button'
            onClick={() => onViewJson(row.original)}
            className='flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-alba-blue-50 text-alba-blue-600 hover:bg-alba-blue-100 transition-colors'
            title='Ver Request/Response'
          >
            <Code className='w-3.5 h-3.5' />
            Ver JSON
          </button>
        );
      },
    },
  ];
}

const REPROCESSABLE_STATUSES = ['error', 'retry_limit_exceeded'];

export function IntegrationDetailsPage({ policyId }: IntegrationDetailsPageProps) {
  const { data, isLoading, error, refetch, isFetching } = useIntegrationDetails(policyId);
  const reprocessMutation = useReprocessIntegration();
  const [jsonModal, setJsonModal] = useState<{
    open: boolean;
    item: IntegrationHistory | null;
  }>({ open: false, item: null });

  const canReprocess = data && REPROCESSABLE_STATUSES.includes(data.status);

  const manualEmission = useManualEmissionAction({
    policyId,
  });

  const releaseToAutomatic = useReleaseToAutomaticAction({ policyId });

  const implantationKit = useImplantationKitDownload(data);

  const handleReprocess = () => {
    if (!confirm('Tem certeza que deseja reprocessar esta integração?')) return;
    reprocessMutation.mutate(policyId);
  };

  const handleViewJson = (item: IntegrationHistory) => {
    setJsonModal({ open: true, item });
  };

  const historyColumns = useMemo(() => createHistoryColumns(handleViewJson), []);

  if (isLoading) {
    return (
      <Page.Content>
        <div className='flex items-center justify-center py-12 text-alba-gray-500'>
          Carregando...
        </div>
      </Page.Content>
    );
  }

  if (error || !data) {
    return (
      <Page.Content>
        <div className='flex items-center justify-center py-12 text-red-500'>
          {error?.message ?? 'Erro ao carregar detalhes da integração'}
        </div>
      </Page.Content>
    );
  }

  const controlStatus = statusConfig[data.status] ?? { label: data.status, colorScheme: 'gray' as const };

  return (
    <>
      <ConfirmActionModal
        isOpen={releaseToAutomatic.isOpen}
        onClose={releaseToAutomatic.close}
        onConfirm={releaseToAutomatic.confirm}
        isLoading={releaseToAutomatic.isPending}
        errorMessage={releaseToAutomatic.errorMessage}
        title='Tentar emissão automática'
        confirmLabel='Tentar emitir'
        description={
          <>
            O Portal tenta emitir a apólice agora pela integração com o i4Pro —
            isso pode levar alguns instantes. Se falhar antes de a apólice ser
            criada no i4Pro, a proposta{' '}
            <strong>volta a aguardar emissão manual</strong>; se falhar depois,
            fica em erro para o <strong>Reprocessar</strong>.
          </>
        }
        warning={
          <>
            Se a apólice <strong>já foi emitida à mão no i4Pro</strong>, não use
            esta opção — ela emitiria uma <strong>segunda apólice</strong>. Nesse
            caso, use <strong>Emitir manualmente</strong> e informe o número.
          </>
        }
      />

      <ManualEmissionModal
        isOpen={manualEmission.isOpen}
        onClose={manualEmission.close}
        onConfirm={manualEmission.confirm}
        isLoading={manualEmission.isPending}
        errorMessage={manualEmission.errorMessage}
        proposalNo={data.proposalNo}
        requiresPolicyPdf={manualEmission.requiresPolicyPdf}
      />

      <JsonViewerModal
        isOpen={jsonModal.open}
        onClose={() => setJsonModal({ open: false, item: null })}
        title={`Payload - ${stepLabels[jsonModal.item?.step ?? ''] ?? jsonModal.item?.step ?? ''}`}
        requestPayload={jsonModal.item?.requestPayload}
        responsePayload={jsonModal.item?.responsePayload}
        traceId={jsonModal.item?.traceId}
        requestId={jsonModal.item?.requestId}
      />

      <Page.Content>
        <div className='space-y-6 pb-6 md:pb-0'>
          {/* Emission Control Details */}
          <div className='bg-white rounded-xl border border-alba-gray-200 p-6'>
            <div className='flex items-center justify-between mb-4'>
              <h2 className='text-lg font-semibold text-alba-black-500'>
                Controle de Emissão
              </h2>
              <div className='flex items-center gap-2'>
                {implantationKit.isVisible && (
                  <button
                    type='button'
                    onClick={implantationKit.download}
                    disabled={implantationKit.isPending}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 hover:bg-alba-gray-50 transition-colors disabled:opacity-50'
                  >
                    <Download className={`w-3.5 h-3.5 ${implantationKit.isPending ? 'animate-pulse' : ''}`} />
                    {implantationKit.isPending ? 'Gerando kit...' : 'Baixar kit de implantação'}
                  </button>
                )}
                {implantationKit.canResend && (
                  <button
                    type='button'
                    onClick={implantationKit.resend}
                    disabled={implantationKit.isResending}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 hover:bg-alba-gray-50 transition-colors disabled:opacity-50'
                  >
                    <Mail className='w-3.5 h-3.5' />
                    {implantationKit.isResending ? 'Reenviando...' : 'Reenviar pacote'}
                  </button>
                )}
                {manualEmission.isVisible && (
                  <button
                    type='button'
                    onClick={manualEmission.open}
                    disabled={manualEmission.isPending}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-alba-blue-500 text-white hover:bg-alba-blue-600 transition-colors disabled:opacity-50'
                  >
                    <FilePlus2 className='w-3.5 h-3.5' />
                    Emitir manualmente
                  </button>
                )}
                {releaseToAutomatic.isVisible && (
                  <button
                    type='button'
                    onClick={releaseToAutomatic.open}
                    disabled={releaseToAutomatic.isPending}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 hover:bg-alba-gray-50 transition-colors disabled:opacity-50'
                  >
                    <Send className='w-3.5 h-3.5' />
                    Tentar emissão automática
                  </button>
                )}
                {canReprocess && (
                  <button
                    type='button'
                    onClick={handleReprocess}
                    disabled={reprocessMutation.isPending}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-alba-blue-500 text-white hover:bg-alba-blue-600 transition-colors disabled:opacity-50'
                  >
                    <RotateCcw className={`w-3.5 h-3.5 ${reprocessMutation.isPending ? 'animate-spin' : ''}`} />
                    {reprocessMutation.isPending ? 'Reprocessando...' : 'Reprocessar'}
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className='flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-alba-gray-300 bg-white text-alba-gray-600 hover:bg-alba-gray-50 transition-colors disabled:opacity-50'
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
              </div>
            </div>

            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  ID Apólice InsureMO
                </label>
                <div className='mt-1'>
                  <CopyableField value={data.insuremoPolicyId} label="ID Apólice InsureMO" truncate />
                </div>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Nº Proposta
                </label>
                <div className='mt-1'>
                  {data.proposalNo
                    ? <CopyableField value={data.proposalNo} label="Nº Proposta" />
                    : <p className='text-sm text-alba-gray-800'>-</p>}
                </div>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Nº Apólice I4Pro
                </label>
                <div className='mt-1'>
                  {data.i4proPolicyNo
                    ? <CopyableField value={data.i4proPolicyNo} label="Nº Apólice I4Pro" />
                    : <p className='text-sm text-alba-gray-800'>-</p>}
                </div>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Status
                </label>
                <div className='mt-1'>
                  <Tag.Root variant='outline' colorScheme={controlStatus.colorScheme} size='sm'>
                    <Tag.Label>{controlStatus.label}</Tag.Label>
                  </Tag.Root>
                </div>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Etapa
                </label>
                <p className='mt-1 text-sm text-alba-gray-800'>
                  {stepLabels[data.step] ?? data.step}
                </p>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Criado em
                </label>
                <p className='mt-1 text-sm text-alba-gray-800'>
                  {formatDateTimeToBrazil(data.createdAt)}
                </p>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Início
                </label>
                <p className='mt-1 text-sm text-alba-gray-800'>
                  {data.startedAt ? formatDateTimeToBrazil(data.startedAt) : '-'}
                </p>
              </div>
              <div>
                <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                  Fim
                </label>
                <p className='mt-1 text-sm text-alba-gray-800'>
                  {data.finishedAt ? formatDateTimeToBrazil(data.finishedAt) : '-'}
                </p>
              </div>
              {data.errorMessage && (
                <div className='col-span-2 md:col-span-4'>
                  <label className='text-xs text-alba-gray-500 font-medium uppercase'>
                    Mensagem de Erro
                  </label>
                  <p className='mt-1 text-sm text-red-600'>
                    {data.errorMessage}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Integration History */}
          <div className='bg-white rounded-xl border border-alba-gray-200 p-6'>
            <h2 className='text-lg font-semibold text-alba-black-500 mb-4'>
              Histórico de Integração
            </h2>
            <div className='space-y-3 md:hidden'>
              {(data.history ?? []).map((item, index) => {
                const config = statusConfig[item.status] ?? {
                  label: item.status,
                  colorScheme: 'gray' as const,
                };
                return (
                  <article
                    key={`${item.createdAt}-${item.step}-${index}`}
                    className='rounded-2xl border border-alba-gray-200 bg-white p-4 shadow-[0px_2px_10px_rgba(15,22,30,0.06)]'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='min-w-0'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          {stepLabels[item.step] ?? item.step}
                        </p>
                        <p className='mt-1 text-xs text-alba-gray-600'>
                          {formatDateTimeToBrazil(item.createdAt)}
                        </p>
                      </div>
                      {Boolean(item.requestPayload || item.responsePayload) && (
                        <button
                          type='button'
                          onClick={() => handleViewJson(item)}
                          className='flex shrink-0 items-center gap-1 rounded-md bg-alba-blue-50 px-2 py-1 text-xs text-alba-blue-600'
                        >
                          <Code className='h-3.5 w-3.5' />
                          JSON
                        </button>
                      )}
                    </div>

                    <div className='my-3 h-px w-full bg-alba-gray-200' />

                    <div className='space-y-2 text-sm'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>Fluxo</span>
                        <span className='text-alba-gray-800'>
                          {flowLabels[item.flow] ?? item.flow}
                        </span>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>Provider</span>
                        <Tag.Root variant='outline' colorScheme='gray' size='sm'>
                          <Tag.Label>
                            {providerLabels[item.provider] ?? item.provider}
                          </Tag.Label>
                        </Tag.Root>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>Status</span>
                        <Tag.Root
                          variant='outline'
                          colorScheme={config.colorScheme}
                          size='sm'
                        >
                          <Tag.Label>{config.label}</Tag.Label>
                        </Tag.Root>
                      </div>
                      {item.errorMessage && (
                        <div>
                          <p className='mb-1 text-xs font-semibold uppercase text-alba-gray-600'>
                            Erro
                          </p>
                          <p className='text-sm text-red-600'>{item.errorMessage}</p>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}

              {!data.history?.length && (
                <div className='rounded-2xl border border-alba-gray-200 bg-white p-6 text-center text-alba-gray-600'>
                  Nenhum histórico encontrado.
                </div>
              )}
            </div>

            <div className='hidden md:block'>
              <DataTable
                loading={isLoading}
                columns={historyColumns}
                data={data.history ?? []}
                totalItems={data.history?.length ?? 0}
                totalPages={1}
                pageSize={100}
                pageSizeOptions={[100]}
              />
            </div>
          </div>
        </div>
      </Page.Content>
    </>
  );
}
